/**
 * AI Quality Monitor Component
 * Features: Filters, satisfaction scores, oracle comparison, conversation viewer
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Star, TrendingUp, Filter, Sparkles, Eye, Loader2,
    ThumbsUp, ThumbsDown
} from 'lucide-react';
import { getAuthHeaders } from '../../utils/api';
import { BentoCard, NeonIcon, API_URL } from './shared';

const AIQualityMonitor = ({ isSpanish, handleRateResponse }) => {
    const [ratings, setRatings] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [oracleFilter, setOracleFilter] = useState('all');
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [conversationLoading, setConversationLoading] = useState(false);
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const headers = await getAuthHeaders();
                
                const statsRes = await fetch(`${API_URL}/api/admin/ai-quality/stats`, {
                    headers, credentials: 'include'
                });
                if (statsRes.ok) {
                    const statsData = await statsRes.json();
                    setStats(statsData);
                }
                
                const params = new URLSearchParams();
                if (filter !== 'all') params.append('rating', filter);
                if (oracleFilter !== 'all') params.append('oracle_type', oracleFilter);
                params.append('limit', '30');
                
                const ratingsRes = await fetch(`${API_URL}/api/admin/ai-quality/ratings?${params}`, {
                    headers, credentials: 'include'
                });
                if (ratingsRes.ok) {
                    const ratingsData = await ratingsRes.json();
                    setRatings(ratingsData.ratings || []);
                }
            } catch (e) {
                console.error('AI Quality fetch error:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [filter, oracleFilter]);
    
    const viewConversation = async (rating) => {
        setConversationLoading(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/admin/ai-quality/conversation/${rating.message_id}`, {
                headers, credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedConversation({ ...rating, conversation: data.conversation });
            }
        } catch (e) {
            console.error('Conversation fetch error:', e);
        } finally {
            setConversationLoading(false);
        }
    };
    
    const satisfactionRate = stats?.total_ratings > 0 
        ? Math.round((stats.total_helpful / stats.total_ratings) * 100) 
        : null;
    
    return (
        <BentoCard className="lg:col-span-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <NeonIcon icon={Star} neonColor="yellow" size="w-4 h-4" />
                    {isSpanish ? 'Monitor de Calidad IA' : 'AI Quality Monitor'}
                </h3>
                
                {satisfactionRate !== null && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5">
                        <TrendingUp className={`w-4 h-4 ${satisfactionRate >= 80 ? 'text-green-400' : satisfactionRate >= 60 ? 'text-yellow-400' : 'text-red-400'}`} style={{ filter: `drop-shadow(0 0 4px ${satisfactionRate >= 80 ? '#00FF7F' : satisfactionRate >= 60 ? '#FFFF00' : '#FF0040'}) drop-shadow(0 0 8px ${satisfactionRate >= 80 ? '#00FF7F' : satisfactionRate >= 60 ? '#FFFF00' : '#FF0040'})` }} />
                        <span className={`text-sm font-bold ${satisfactionRate >= 80 ? 'text-green-400' : satisfactionRate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {satisfactionRate}%
                        </span>
                        <span className="text-xs text-white/50">
                            {isSpanish ? 'satisfacción' : 'satisfaction'}
                        </span>
                    </div>
                )}
            </div>
            
            {stats?.by_oracle && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {['cosmos', 'love', 'dream'].map(oracle => {
                        const oracleStats = stats.by_oracle[oracle] || { helpful: 0, total: 0 };
                        const rate = oracleStats.total > 0 ? Math.round((oracleStats.helpful / oracleStats.total) * 100) : null;
                        return (
                            <div key={oracle} className="p-2 bg-white/5 rounded-lg text-center">
                                <div className="text-xs text-white/50 capitalize mb-1">{oracle}</div>
                                <div className={`text-lg font-bold ${rate === null ? 'text-white/30' : rate >= 80 ? 'text-green-400' : rate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {rate !== null ? `${rate}%` : '—'}
                                </div>
                                <div className="text-[10px] text-white/30">{oracleStats.total} {isSpanish ? 'votos' : 'ratings'}</div>
                            </div>
                        );
                    })}
                </div>
            )}
            
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                    <NeonIcon icon={Filter} neonColor="cyan" size="w-3 h-3" className="ml-2" />
                    {[
                        { value: 'all', label: isSpanish ? 'Todos' : 'All' },
                        { value: 'helpful', label: '👍', color: 'green' },
                        { value: 'not_helpful', label: '👎', color: 'red' }
                    ].map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setFilter(opt.value)}
                            className={`px-2 py-1 rounded text-xs transition-colors ${
                                filter === opt.value 
                                    ? opt.color === 'green' ? 'bg-green-500/30 text-green-400'
                                    : opt.color === 'red' ? 'bg-red-500/30 text-red-400'
                                    : 'bg-primary/30 text-primary'
                                    : 'text-white/50 hover:text-white'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                
                <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                    <NeonIcon icon={Sparkles} neonColor="magenta" size="w-3 h-3" className="ml-2" />
                    {[
                        { value: 'all', label: isSpanish ? 'Todos' : 'All' },
                        { value: 'cosmos', label: '🌌' },
                        { value: 'love', label: '💕' },
                        { value: 'dream', label: '🌙' }
                    ].map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setOracleFilter(opt.value)}
                            className={`px-2 py-1 rounded text-xs transition-colors ${
                                oracleFilter === opt.value 
                                    ? 'bg-primary/30 text-primary'
                                    : 'text-white/50 hover:text-white'
                            }`}
                            title={opt.value !== 'all' ? opt.value : ''}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
            
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
            ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {ratings.map((rating, i) => (
                        <div 
                            key={i} 
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                rating.rating === 'helpful' 
                                    ? 'bg-green-500/10 border border-green-500/20 hover:bg-green-500/15' 
                                    : 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/15'
                            }`}
                            onClick={() => viewConversation(rating)}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg">{rating.rating === 'helpful' ? '👍' : '👎'}</span>
                                    <span className="text-xs text-primary uppercase">{rating.oracle_type}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Eye className="w-3 h-3 text-white/30" />
                                    <span className="text-xs text-white/30">
                                        {new Date(rating.timestamp).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                            <p className="text-sm text-white/60 line-clamp-2">
                                {rating.context || isSpanish ? 'Click para ver conversación' : 'Click to view conversation'}
                            </p>
                        </div>
                    ))}
                    {ratings.length === 0 && (
                        <p className="text-white/40 text-sm text-center py-8">
                            {isSpanish ? 'No hay valoraciones con estos filtros' : 'No ratings match these filters'}
                        </p>
                    )}
                </div>
            )}
            
            <AnimatePresence>
                {selectedConversation && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={(e) => e.target === e.currentTarget && setSelectedConversation(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-[#12151c] border border-primary/30 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{selectedConversation.rating === 'helpful' ? '👍' : '👎'}</span>
                                    <div>
                                        <h3 className="text-lg font-bold text-white capitalize">
                                            {selectedConversation.oracle_type} Oracle
                                        </h3>
                                        <p className="text-sm text-white/50">
                                            {new Date(selectedConversation.timestamp).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedConversation(null)} className="text-white/50 hover:text-white">✕</button>
                            </div>
                            
                            {conversationLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                                </div>
                            ) : selectedConversation.conversation ? (
                                <div className="space-y-3">
                                    {selectedConversation.conversation.map((msg, i) => (
                                        <div 
                                            key={i}
                                            className={`p-3 rounded-lg ${
                                                msg.role === 'user' 
                                                    ? 'bg-blue-500/10 border border-blue-500/20 ml-8' 
                                                    : 'bg-primary/10 border border-primary/20 mr-8'
                                            }`}
                                        >
                                            <div className="text-xs text-white/40 mb-1">
                                                {msg.role === 'user' ? '👤 User' : '🔮 Oracle'}
                                            </div>
                                            <p className="text-sm text-white/80 whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-white/40 text-center py-8">
                                    {isSpanish ? 'Conversación no disponible' : 'Conversation not available'}
                                </p>
                            )}
                            
                            <div className="mt-6 pt-4 border-t border-white/10">
                                <p className="text-sm text-white/50 mb-2">
                                    {isSpanish ? 'Tu valoración de esta respuesta:' : 'Your rating of this response:'}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            handleRateResponse(selectedConversation.message_id, 'good');
                                            setSelectedConversation(null);
                                        }}
                                        className="flex-1 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 flex items-center justify-center gap-2"
                                    >
                                        <ThumbsUp className="w-4 h-4" />
                                        {isSpanish ? 'Buena' : 'Good'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleRateResponse(selectedConversation.message_id, 'bad');
                                            setSelectedConversation(null);
                                        }}
                                        className="flex-1 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center justify-center gap-2"
                                    >
                                        <ThumbsDown className="w-4 h-4" />
                                        {isSpanish ? 'Mala' : 'Bad'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </BentoCard>
    );
};

export default AIQualityMonitor;
